import React, { useContext, useMemo, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import ClientNavbar from "../layout/ClientNavbar";
import LocContext from "../context/locContext";
import { submitMutualFundInvestorProfile } from "../../api/bseApi";
import {
  isInvestorOnboardingComplete,
  markInvestorOnboardingSubmitted,
} from "../../utils/clientInvestorProfile";

const STEPS = [
  { id: 0, title: "Account", subtitle: "Client code and account preferences" },
  { id: 1, title: "Personal details", subtitle: "Primary holder identity and contact" },
  { id: 2, title: "Address", subtitle: "Communication address" },
  { id: 3, title: "Bank", subtitle: "Primary bank account" },
  { id: 4, title: "Demat (CDSL)", subtitle: "Demat account for mutual fund holdings" },
  { id: 5, title: "Document", subtitle: "Signed application form (PDF)" },
];

const textOnly = /^[A-Za-z\s]+$/;
const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
const ifscRegex = /^[A-Z]{4}0[0-9]{6}$/;
const digits10 = /^[0-9]{10}$/;

function sanitizeErrorMessage(raw) {
  if (raw == null) return "Something went wrong. Please try again.";
  let s = String(raw);
  s = s.replace(/\/v2\/add_ucc/gi, "");
  s = s.replace(/add_ucc/gi, "registration");
  return s.trim() || "Something went wrong. Please try again.";
}

export default function ClientUccWizard() {
  const { userData, refreshUserData } = useContext(LocContext);
  const userId = userData?.id;
  const navigate = useNavigate();

  const alreadyDone = useMemo(
    () => isInvestorOnboardingComplete(userData),
    [userData]
  );

  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    member_id: (process.env.REACT_APP_BSE_MEMBER_ID || "63164").trim(),
    client_code: "",
    holding_nature: "SI",
    tax_code: "01",
    rdmp_idcw_pay_mode: "02",
    comm_mode: "E",
    onboarding: "Z",
    first_name: "",
    last_name: "",
    dob: "",
    gender: "M",
    occ_code: "02",
    auth_mode: "M",
    kyc_type: "K",
    pan: "",
    phone: "",
    email: "",
    country_code: "91",
    addr1: "",
    city: "",
    state: "",
    postalcode: "",
    country: "INDIA",
    ifsc_code: "",
    bank_acc_num: "",
    bank_acc_type: "SB",
    dp_id: "",
    client_id: "",
    aofBase64: "",
    aofReady: false,
  });

  const update = useCallback((patch) => {
    setForm((prev) => ({ ...prev, ...patch }));
  }, []);

  const validateStep = (s) => {
    switch (s) {
      case 0: {
        const mid = String(form.member_id || "").trim();
        if (!mid || !/^[0-9]+$/.test(mid)) return "Enter a valid BSE member ID (digits only).";
        const cc = form.client_code.trim();
        if (!cc || cc.length < 4) return "Enter a valid client code (min 4 characters).";
        if (!/^[A-Za-z0-9_-]+$/.test(cc)) return "Client code may only contain letters, numbers, hyphen and underscore.";
        return null;
      }
      case 1: {
        if (!form.first_name.trim() || !textOnly.test(form.first_name.trim()))
          return "First name is required (letters only, no digits).";
        if (!form.last_name.trim() || !textOnly.test(form.last_name.trim()))
          return "Last name is required (letters only).";
        if (!form.dob) return "Date of birth is required.";
        const p = form.pan.trim().toUpperCase();
        if (!panRegex.test(p)) return "Enter a valid PAN (e.g. ABCDE1234F).";
        if (!digits10.test(form.phone.replace(/\D/g, "")))
          return "Enter a valid 10-digit mobile number.";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
          return "Enter a valid email address.";
        return null;
      }
      case 2: {
        if (!form.addr1.trim()) return "Address line 1 is required.";
        if (!form.city.trim()) return "City is required.";
        if (!form.state.trim()) return "State is required.";
        if (!/^[0-9]{6}$/.test(form.postalcode.trim())) return "PIN code must be 6 digits.";
        return null;
      }
      case 3: {
        if (!ifscRegex.test(form.ifsc_code.trim().toUpperCase()))
          return "Enter a valid IFSC code.";
        if (!/^[0-9]{9,18}$/.test(form.bank_acc_num.trim()))
          return "Bank account number must be 9–18 digits.";
        return null;
      }
      case 4: {
        if (!/^[0-9]{8}$/.test(form.dp_id.trim()))
          return "DP ID must be exactly 8 digits.";
        if (!/^[0-9]{8}$/.test(form.client_id.trim()))
          return "Beneficiary client ID must be exactly 8 digits.";
        return null;
      }
      case 5: {
        if (!form.aofReady || !form.aofBase64) return "Please upload a PDF application form.";
        return null;
      }
      default:
        return null;
    }
  };

  const next = () => {
    const err = validateStep(step);
    if (err) {
      Swal.fire({ icon: "warning", title: "Check this step", text: err });
      return;
    }
    setStep((x) => Math.min(STEPS.length - 1, x + 1));
  };

  const back = () => setStep((x) => Math.max(0, x - 1));

  const onPdf = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/\.pdf$/i.test(file.name)) {
      Swal.fire({ icon: "warning", title: "Invalid file", text: "Please upload a PDF file." });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || "");
      const base64 = dataUrl.includes(",") ? dataUrl.split(",")[1] : "";
      if (!base64) {
        Swal.fire({ icon: "error", title: "Read failed", text: "Could not read the file." });
        return;
      }
      update({ aofBase64: base64.replace(/\s+/g, "").trim(), aofReady: true });
    };
    reader.onerror = () =>
      Swal.fire({ icon: "error", title: "Error", text: "Failed to read file." });
    reader.readAsDataURL(file);
  };

  const buildSubmitPayload = () => {
    const cleanedBlob = String(form.aofBase64 || "")
      .replace(/^data:[^;]+;base64,/i, "")
      .replace(/\s+/g, "")
      .trim();
    let decodedSize = 0;
    try {
      decodedSize = atob(cleanedBlob).length;
    } catch {
      decodedSize = 0;
    }
    if (!cleanedBlob || decodedSize < 4) {
      throw new Error("Invalid or empty PDF. Please upload the file again.");
    }

    const pan = form.pan.trim().toUpperCase();
    const phoneDigits = form.phone.replace(/\D/g, "").slice(-10);
    const memberId = String(form.member_id || "63164").trim() || "63164";

    return {
      data: {
        member: { member_id: memberId },
        investor: { client_code: form.client_code.trim() },
        holding_nature: form.holding_nature,
        tax_code: form.tax_code,
        rdmp_idcw_pay_mode: form.rdmp_idcw_pay_mode,
        is_client_physical: false,
        is_client_demat: true,
        is_nomination_opted: false,
        comm_mode: form.comm_mode,
        onboarding: form.onboarding,
        holder: [
          {
            holder_rank: "1",
            occ_code: form.occ_code,
            auth_mode: form.auth_mode,
            is_pan_exempt: false,
            kyc_type: form.kyc_type,
            person: {
              first_name: form.first_name.trim(),
              last_name: form.last_name.trim(),
              dob: form.dob,
              gender: form.gender,
            },
            contact: [
              {
                contact_number: phoneDigits,
                country_code: form.country_code.replace(/\D/g, "") || "91",
                whose_contact_number: "SE",
                email_address: form.email.trim(),
                whose_email_address: "SE",
                contact_type: "PR",
              },
            ],
            identifier: [
              {
                identifier_type: "pan",
                identifier_number: pan,
              },
            ],
          },
        ],
        comm_addr: {
          address_line_1: form.addr1.trim(),
          city: form.city.trim(),
          state: form.state.trim(),
          postalcode: form.postalcode.trim(),
          country: form.country.trim() || "INDIA",
        },
        bank_account: [
          {
            ifsc_code: form.ifsc_code.trim().toUpperCase(),
            bank_acc_num: form.bank_acc_num.trim(),
            bank_acc_type: form.bank_acc_type,
            account_owner: "SELF",
          },
        ],
        depository: [
          {
            depository_code: "CDSL",
            dp_id: form.dp_id.trim(),
            client_id: form.client_id.trim(),
            bank_account: form.bank_acc_num.trim(),
            account_owner: "SELF",
            is_default: true,
          },
        ],
        identifiers: [
          {
            identifier_type: "aof",
            file_name: "aof.pdf",
            file_blob: cleanedBlob,
            file_size: decodedSize,
            identifier_size: decodedSize,
          },
        ],
      },
    };
  };

  const submit = async () => {
    const err = validateStep(5);
    if (err) {
      Swal.fire({ icon: "warning", title: "Almost there", text: err });
      return;
    }
    setSubmitting(true);
    try {
      const payload = buildSubmitPayload();
      const res = await submitMutualFundInvestorProfile(payload);
      const data = res?.data;
      const st = String(data?.status ?? "").toLowerCase();
      const msgs = Array.isArray(data?.messages) ? data.messages : [];
      if (st === "error" || msgs.length > 0) {
        const first = msgs[0] || {};
        const detail = [first.errcode, first.field, Array.isArray(first.vals) ? first.vals.join(", ") : ""]
          .filter(Boolean)
          .join(" — ");
        throw {
          response: {
            status: 400,
            data: {
              message: detail || first.message || "The exchange did not accept this registration.",
              data: { messages: msgs, bse: data },
            },
          },
        };
      }
      try {
        await markInvestorOnboardingSubmitted();
        if (refreshUserData) await refreshUserData();
      } catch (statusErr) {
        console.error(statusErr);
        await Swal.fire({
          icon: "warning",
          title: "Registration sent",
          text:
            "Your registration was accepted by the exchange, but we could not update your account status. Please log out and log in again, or contact support.",
        });
        navigate("/overview", { replace: true });
        return;
      }
      await Swal.fire({
        icon: "success",
        title: "Registration submitted",
        html: `
          <p style="text-align:left;margin:0 0 12px;">
            Thank you. Your mutual fund investor registration has been sent to the exchange.
          </p>
          <p style="text-align:left;margin:0 0 12px;">
            <strong>Please check your email</strong> (the address you entered) for updates from your AMC or registrar.
          </p>
          <p style="text-align:left;margin:0;">
            Your account may show as <strong>authentication pending</strong> until the exchange and your distributor complete verification. This can take some time — you will be notified when you can transact.
          </p>
        `,
        confirmButtonText: "Go to dashboard",
      });
      navigate("/overview", { replace: true });
    } catch (error) {
      if (error instanceof Error && !error.response) {
        Swal.fire({ icon: "warning", title: "Check document", text: error.message });
        return;
      }
      const api = error?.response?.data;
      const missing = api?.data?.missing_fields;
      const hint = api?.data?.hint;
      const idErr = api?.data?.identifier_errors;
      const bseMsgs = api?.data?.bse?.messages || api?.data?.messages;
      const msg =
        api?.message ||
        (Array.isArray(missing) && missing.length
          ? `Missing: ${missing.join(", ")}`
          : null) ||
        (Array.isArray(bseMsgs) && bseMsgs[0]?.errcode) ||
        api?.data?.messages?.[0]?.errcode ||
        api?.data?.messages?.[0]?.field ||
        error?.message;
      Swal.fire({
        icon: "error",
        title: "Could not submit",
        html: `<p>${sanitizeErrorMessage(msg)}</p>${
          hint ? `<p class="small text-muted mt-2">${hint}</p>` : ""
        }${
          Array.isArray(idErr) && idErr.length
            ? `<pre class="small text-start mt-2">${JSON.stringify(idErr, null, 2)}</pre>`
            : ""
        }${
          Array.isArray(bseMsgs) && bseMsgs.length
            ? `<pre class="small text-start mt-2">${JSON.stringify(bseMsgs, null, 2)}</pre>`
            : ""
        }`,
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (alreadyDone) {
    return (
      <>
        <ClientNavbar />
        <div className="page-content" style={{ background: "#f1f5f9", minHeight: "70vh" }}>
          <div className="card shadow-sm p-4 mx-auto" style={{ maxWidth: 640, marginTop: 32 }}>
            <h4 className="text-primary mb-3">Investor onboarding</h4>
            <p className="text-muted mb-4">
              You have already submitted your mutual fund investor registration. While exchange
              authentication is pending, you can explore funds or update your profile.
            </p>
            <div className="d-flex flex-wrap gap-2">
              <Link to="/overview" className="btn btn-primary">
                Dashboard
              </Link>
              <Link to="/explore" className="btn btn-outline-primary">
                Explore funds
              </Link>
            </div>
          </div>
        </div>
      </>
    );
  }

  const progressPct = ((step + 1) / STEPS.length) * 100;

  return (
    <>
      <ClientNavbar />
      <div className="page-content" style={{ background: "#f1f5f9", paddingBottom: 48 }}>
        <div className="card shadow-sm p-4 mb-3">
          <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
            <div>
              <h3 className="text-primary mb-1">Mutual fund investor onboarding</h3>
              <p className="text-muted mb-0 small">
                Step {step + 1} of {STEPS.length}: {STEPS[step].title}
              </p>
            </div>
            <Link to="/overview" className="btn btn-sm btn-outline-secondary">
              Save for later
            </Link>
          </div>
          <div
            className="mt-3 rounded-pill"
            style={{ height: 8, background: "#e2e8f0", overflow: "hidden" }}
          >
            <div
              style={{
                width: `${progressPct}%`,
                height: "100%",
                background: "linear-gradient(90deg,#0d9488,#14b8a6)",
                transition: "width 0.25s ease",
              }}
            />
          </div>
        </div>

        <div className="card shadow-sm p-4" style={{ maxWidth: 720, margin: "0 auto" }}>
          <p className="text-muted small mb-4">{STEPS[step].subtitle}</p>

          {step === 0 && (
            <div className="row g-3">
              <div className="col-12 col-md-6">
                <label className="form-label">BSE member ID</label>
                <input
                  className="form-control"
                  value={form.member_id}
                  onChange={(e) => update({ member_id: e.target.value.replace(/\D/g, "").slice(0, 12) })}
                  placeholder="e.g. 63164"
                  inputMode="numeric"
                  autoComplete="off"
                />
                <div className="form-text">Same member code your distributor uses with BSE Star MF (default 63164).</div>
              </div>
              <div className="col-12 col-md-6">
                <label className="form-label">Client code</label>
                <input
                  className="form-control"
                  value={form.client_code}
                  onChange={(e) => update({ client_code: e.target.value })}
                  placeholder="Unique code for your mutual fund account"
                  autoComplete="off"
                />
                <div className="form-text">
                  Choose a unique code. If the exchange reports a duplicate, pick another.
                </div>
              </div>
              <div className="col-md-6">
                <label className="form-label">Tax status</label>
                <select
                  className="form-select"
                  value={form.tax_code}
                  onChange={(e) => update({ tax_code: e.target.value })}
                >
                  <option value="01">Resident individual</option>
                  <option value="02">NRI – NRE (repatriable)</option>
                  <option value="03">NRI – NRO (non-repatriable)</option>
                </select>
              </div>
              <div className="col-md-6">
                <label className="form-label">Holding nature</label>
                <select
                  className="form-select"
                  value={form.holding_nature}
                  onChange={(e) => update({ holding_nature: e.target.value })}
                >
                  <option value="SI">Single</option>
                  <option value="JO">Joint</option>
                </select>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label">First name</label>
                <input
                  className="form-control"
                  value={form.first_name}
                  onChange={(e) => update({ first_name: e.target.value })}
                  placeholder="As per PAN"
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Last name</label>
                <input
                  className="form-control"
                  value={form.last_name}
                  onChange={(e) => update({ last_name: e.target.value })}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Date of birth</label>
                <input
                  type="date"
                  className="form-control"
                  value={form.dob}
                  onChange={(e) => update({ dob: e.target.value })}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Gender</label>
                <select
                  className="form-select"
                  value={form.gender}
                  onChange={(e) => update({ gender: e.target.value })}
                >
                  <option value="M">Male</option>
                  <option value="F">Female</option>
                  <option value="O">Other</option>
                </select>
              </div>
              <div className="col-md-6">
                <label className="form-label">PAN</label>
                <input
                  className="form-control text-uppercase"
                  value={form.pan}
                  onChange={(e) => update({ pan: e.target.value.toUpperCase() })}
                  maxLength={10}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Mobile</label>
                <input
                  className="form-control"
                  value={form.phone}
                  onChange={(e) => update({ phone: e.target.value.replace(/\D/g, "").slice(0, 10) })}
                  inputMode="numeric"
                />
              </div>
              <div className="col-12">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  className="form-control"
                  value={form.email}
                  onChange={(e) => update({ email: e.target.value })}
                />
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="row g-3">
              <div className="col-12">
                <label className="form-label">Address line 1</label>
                <input
                  className="form-control"
                  value={form.addr1}
                  onChange={(e) => update({ addr1: e.target.value })}
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">City</label>
                <input
                  className="form-control"
                  value={form.city}
                  onChange={(e) => update({ city: e.target.value })}
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">State / UT</label>
                <input
                  className="form-control"
                  value={form.state}
                  onChange={(e) => update({ state: e.target.value })}
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">PIN code</label>
                <input
                  className="form-control"
                  value={form.postalcode}
                  onChange={(e) => update({ postalcode: e.target.value.replace(/\D/g, "").slice(0, 6) })}
                  inputMode="numeric"
                />
              </div>
              <div className="col-12">
                <label className="form-label">Country</label>
                <input
                  className="form-control"
                  value={form.country}
                  onChange={(e) => update({ country: e.target.value })}
                />
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="row g-3">
              <div className="col-12">
                <label className="form-label">IFSC</label>
                <input
                  className="form-control text-uppercase"
                  value={form.ifsc_code}
                  onChange={(e) => update({ ifsc_code: e.target.value.toUpperCase() })}
                  maxLength={11}
                />
              </div>
              <div className="col-12">
                <label className="form-label">Bank account number</label>
                <input
                  className="form-control"
                  value={form.bank_acc_num}
                  onChange={(e) => update({ bank_acc_num: e.target.value.replace(/\D/g, "") })}
                  inputMode="numeric"
                />
              </div>
              <div className="col-12">
                <label className="form-label">Account type</label>
                <select
                  className="form-select"
                  value={form.bank_acc_type}
                  onChange={(e) => update({ bank_acc_type: e.target.value })}
                >
                  <option value="SB">Savings</option>
                  <option value="CB">Current</option>
                </select>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="row g-3">
              <div className="col-12">
                <p className="small text-muted mb-0">
                  Enter your CDSL demat details (8-digit DP ID and 8-digit beneficiary ID). These must match your
                  depository statement.
                </p>
              </div>
              <div className="col-md-6">
                <label className="form-label">DP ID</label>
                <input
                  className="form-control"
                  value={form.dp_id}
                  onChange={(e) => update({ dp_id: e.target.value.replace(/\D/g, "").slice(0, 8) })}
                  inputMode="numeric"
                  maxLength={8}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Beneficiary / client ID</label>
                <input
                  className="form-control"
                  value={form.client_id}
                  onChange={(e) => update({ client_id: e.target.value.replace(/\D/g, "").slice(0, 8) })}
                  inputMode="numeric"
                  maxLength={8}
                />
              </div>
            </div>
          )}

          {step === 5 && (
            <div>
              <label className="form-label">Application form (PDF)</label>
              <input
                type="file"
                className="form-control"
                accept="application/pdf"
                onChange={onPdf}
              />
              {form.aofReady && (
                <p className="text-success small mt-2 mb-0">PDF attached and ready to submit.</p>
              )}
              <p className="text-muted small mt-3 mb-0">
                Upload a clear scan of your signed mutual fund application / AOF. The file must be a valid PDF.
              </p>
            </div>
          )}

          <div className="d-flex justify-content-between mt-4 pt-3 border-top">
            <button
              type="button"
              className="btn btn-outline-secondary"
              disabled={step === 0}
              onClick={back}
            >
              Back
            </button>
            {step < STEPS.length - 1 ? (
              <button type="button" className="btn btn-primary" onClick={next}>
                Continue
              </button>
            ) : (
              <button
                type="button"
                className="btn btn-success"
                disabled={submitting}
                onClick={submit}
              >
                {submitting ? "Submitting…" : "Submit registration"}
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
